import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  AddCircleOutline,
  Edit,
  Delete,
  Visibility as VisibilityIcon,
  Assignment,
  RateReview,
  CheckCircleOutline,
  EditNote,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Grid,
  MenuItem,
  Select,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import AppraisalViewController from "../../redux/actions/AppraisalAction";
import AppraisalCycleFormModal from "./AddAppraisal";
import AppraisalViewModal from "./AppraisalView";
import AppraisalHistoryViewModal from "./AppraisalHistoryView";
import AppraisalMobileView from "./AppraisalMobileView";
import usePermission from "../../utils/usePermission";
import { toolbarButtonSx, BRAND } from "../../components/auth/authTheme";
import "../../styles/styles.css";
import "../../styles/layout.css";

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft", className: "draft" },
  { value: "in_review", label: "In Review", className: "in_review" },
  { value: "approved", label: "Approved", className: "approved" },
  { value: "rejected", label: "Rejected", className: "rejected" },
  { value: "applied", label: "Applied to Payroll", className: "applied" },
];

const getRowKey = (row) => row.id ?? row.employee_id;

const AppraisalCycleList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [activeTab, setActiveTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editCycle, setEditCycle] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEmployeeForHistory, setSelectedEmployeeForHistory] =
    useState(null);

  const {
    getAppraisalCycle,
    deleteAppraisalCycle,
    updateAppraisalCycle,
    getAppraisalHistory,
  } = AppraisalViewController();
  const { appraisal, appraisal_history } = useSelector(
    (state) => state.apprisalcycle
  );
  const { hasPermission } = usePermission();

  useEffect(() => {
    dispatch(getAppraisalCycle());
    dispatch(getAppraisalHistory());
  }, [dispatch]);

  const canEditStatus = hasPermission("appraisal_cycle", "edit");

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSelectedRows([]);
    gridRef.current?.deselectAll?.();
  };

  const handleDelete = useCallback(
    (id) => dispatch(deleteAppraisalCycle(id)),
    [dispatch]
  );

  const handleEdit = useCallback((cycle) => {
    setEditCycle(cycle);
    setShowModal(true);
  }, []);

  const handleView = useCallback((cycle) => {
    setSelectedCycle(cycle);
    setViewModalOpen(true);
  }, []);

  const handleHistoryView = useCallback((employee) => {
    setSelectedEmployeeForHistory(employee);
    setHistoryModalOpen(true);
  }, []);

  const handleToggleSelect = useCallback((row) => {
    const key = getRowKey(row);
    setSelectedRows((prev) => {
      const exists = prev.some((item) => getRowKey(item) === key);
      if (exists) {
        return prev.filter((item) => getRowKey(item) !== key);
      }
      return [...prev, row];
    });
  }, []);

  const handleMobileTabChange = useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    setSelectedRows([]);
    setSearchTerm("");
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedRows([]);
    gridRef.current?.deselectAll?.();
  }, []);

  const handleStatusChange = useCallback(
    (cycle, newStatus) => {
      dispatch(updateAppraisalCycle(cycle.id, { ...cycle, status: newStatus }));
    },
    [dispatch, updateAppraisalCycle]
  );

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) {
      alert("Please select rows to export.");
      return;
    }

    const exportColumns =
      activeTab === 0
        ? [
            "employee_name",
            "cycle_start",
            "cycle_end",
            "hike_percentage",
            "effective_from",
            "status",
            "review_count",
          ]
        : ["employee_id", "employee_name", "designation"];

    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      exportColumns.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });

    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(
      workbook,
      activeTab === 0 ? "Appraisals.xlsx" : "AppraisalHistory.xlsx"
    );
  }, [activeTab, selectedRows]);

  const openAddModal = useCallback(() => {
    setEditCycle(null);
    setShowModal(true);
  }, []);

  const filteredAppraisals = useMemo(() => {
    const list = appraisal || [];
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((item) =>
      `${item.employee_name || ""}`.toLowerCase().includes(q)
    );
  }, [appraisal, searchTerm]);

  const filteredHistory = useMemo(() => {
    const list = appraisal_history || [];
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((item) =>
      `${item.employee_name || ""} ${item.employee_id || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [appraisal_history, searchTerm]);

  const stats = useMemo(() => {
    const list = appraisal || [];
    return {
      total: list.length,
      inReview: list.filter((a) => a.status === "in_review").length,
      approved: list.filter((a) => a.status === "approved").length,
      draft: list.filter((a) => a.status === "draft").length,
      historyCount: (appraisal_history || []).length,
    };
  }, [appraisal, appraisal_history]);

  const renderStatusCell = useCallback(
    (params) => {
      const status = params.data.status;
      const id = params.data.id;
      const meta = STATUS_OPTIONS.find((s) => s.value === status);

      const handleChange = (e) => {
        const newStatus = e.target.value;
        dispatch(updateAppraisalCycle(id, { ...params.data, status: newStatus }));
      };

      if (canEditStatus) {
        return (
          <Select
            size="small"
            value={status}
            onChange={handleChange}
            sx={{
              minWidth: 140,
              fontSize: "0.8125rem",
              fontWeight: 600,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.border },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: BRAND.orange,
              },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );
      }

      return (
        <span className={`appraisal-status-badge ${meta?.className || "draft"}`}>
          {meta?.label || status}
        </span>
      );
    },
    [canEditStatus, dispatch]
  );

  const appraisalColumnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee",
        field: "employee_name",
        flex: 1.5,
        minWidth: 160,
      },
      {
        headerName: "Appraisal Period",
        flex: 1.5,
        minWidth: 180,
        valueGetter: (params) => {
          const start = params.data.cycle_start || "";
          const end = params.data.cycle_end || "";
          return start && end ? `${start} – ${end}` : "";
        },
      },
      {
        headerName: "Hike (%)",
        field: "hike_percentage",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        suppressSizeToFit: true,
      },
      {
        headerName: "Effective From",
        field: "effective_from",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        suppressSizeToFit: true,
      },
      {
        headerName: "Status",
        field: "status",
        width: 160,
        minWidth: 150,
        maxWidth: 180,
        suppressSizeToFit: true,
        cellRenderer: renderStatusCell,
      },
      {
        headerName: "Reviews",
        field: "review_count",
        width: 100,
        minWidth: 90,
        maxWidth: 110,
        suppressSizeToFit: true,
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 130,
        minWidth: 130,
        maxWidth: 130,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Tooltip title="View">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleView(params.data)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(params.data)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: "#d32f2f" }}
                onClick={() => handleDelete(params.data.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleDelete, handleEdit, handleView, renderStatusCell]
  );

  const historyColumnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee ID",
        field: "employee_id",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        suppressSizeToFit: true,
      },
      {
        headerName: "Employee Name",
        field: "employee_name",
        flex: 1.5,
        minWidth: 160,
      },
      {
        headerName: "Designation",
        field: "designation",
        flex: 1,
        minWidth: 140,
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="View History">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleHistoryView(params.data)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleHistoryView]
  );

  const statTiles =
    activeTab === 0 ? (
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <div className="stat-card stat-card-compact">
            <div className="avatar rounded-circle bg-primary">
              <Assignment className="white-icon" />
            </div>
            <div className="stat-card-compact-body">
              <h6>Total Cycles</h6>
              <h3 className="value">{stats.total}</h3>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="stat-card stat-card-compact">
            <div className="avatar rounded-circle bg-info">
              <RateReview className="white-icon" />
            </div>
            <div className="stat-card-compact-body">
              <h6>In Review</h6>
              <h3 className="value">{stats.inReview}</h3>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="stat-card stat-card-compact">
            <div className="avatar rounded-circle bg-primary">
              <CheckCircleOutline className="white-icon" />
            </div>
            <div className="stat-card-compact-body">
              <h6>Approved</h6>
              <h3 className="value">{stats.approved}</h3>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className="stat-card stat-card-compact">
            <div className="avatar rounded-circle bg-danger">
              <EditNote className="white-icon" />
            </div>
            <div className="stat-card-compact-body">
              <h6>Draft</h6>
              <h3 className="value">{stats.draft}</h3>
            </div>
          </div>
        </Grid>
      </Grid>
    ) : (
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <div className="stat-card stat-card-compact">
            <div className="avatar rounded-circle bg-primary">
              <Assignment className="white-icon" />
            </div>
            <div className="stat-card-compact-body">
              <h6>Employees with History</h6>
              <h3 className="value">{stats.historyCount}</h3>
            </div>
          </div>
        </Grid>
      </Grid>
    );

  return (
    <>
      {isMobile ? (
        <AppraisalMobileView
          activeTab={activeTab}
          setActiveTab={handleMobileTabChange}
          appraisals={filteredAppraisals}
          history={filteredHistory}
          stats={stats}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onHistoryView={handleHistoryView}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          onClearSelection={handleClearSelection}
          canEditStatus={canEditStatus}
          onStatusChange={handleStatusChange}
        />
      ) : (
      <ListPageLayout
        title="Appraisal Cycle"
        subtitle={
          activeTab === 0
            ? "Manage employee appraisal cycles, hikes, and review status"
            : "Browse historical appraisal records by employee"
        }
        headerAction={
          activeTab === 0 ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={() => {
                setEditCycle(null);
                setShowModal(true);
              }}
              sx={toolbarButtonSx}
            >
              Add Appraisal
            </Button>
          ) : null
        }
        toolbarRight={
          <>
            <ExportButton
              gridRef={gridRef}
              fileName={
                activeTab === 0 ? "Appraisals.xlsx" : "AppraisalHistory.xlsx"
              }
              exportColumns={
                activeTab === 0
                  ? [
                      "employee_name",
                      "cycle_start",
                      "cycle_end",
                      "hike_percentage",
                      "effective_from",
                      "status",
                      "review_count",
                    ]
                  : ["employee_id", "employee_name", "designation"]
              }
              disabled={selectedRows.length === 0}
              onSelectionCleared={() => setSelectedRows([])}
            />
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <PageSizeSelector
              pageSize={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            />
          </>
        }
        stats={statTiles}
        tabs={
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Appraisals" />
            <Tab label="History" />
          </Tabs>
        }
      >
        <div className="list-page-grid">
          {activeTab === 0 ? (
            <ReusableGrid
              rowData={filteredAppraisals}
              columnDefs={appraisalColumnDefs}
              gridRef={gridRef}
              pagination
              pageSize={pageSize}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No appraisal cycles yet"
              emptyHint='Click "Add Appraisal" to create the first cycle.'
            />
          ) : (
            <ReusableGrid
              rowData={filteredHistory}
              columnDefs={historyColumnDefs}
              gridRef={gridRef}
              pagination
              pageSize={pageSize}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No appraisal history found"
              emptyHint="History appears once employees have completed appraisal cycles."
            />
          )}
        </div>
      </ListPageLayout>
      )}

      {showModal && (
        <AppraisalCycleFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editCycle}
        />
      )}

      <AppraisalViewModal
        show={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        selectedCycle={selectedCycle}
      />

      <AppraisalHistoryViewModal
        show={historyModalOpen}
        handleClose={() => setHistoryModalOpen(false)}
        selectedEmployee={selectedEmployeeForHistory}
      />
    </>
  );
};

export default AppraisalCycleList;
